import type { Client, InboxNotification, InboxSeverity } from "@khazzin/api-client";
import type { Locale, Translate } from "@khazzin/i18n";
import { formatDateTime } from "@khazzin/i18n/dates";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Icon } from "../icons/Icon";
import { Button } from "../primitives/Button";
import { Badge } from "../primitives/Display";
import { SegmentedControl } from "../primitives/SegmentedControl";
import { Select } from "../primitives/Select";
import { Switch } from "../primitives/Selection";
import { cx } from "../styles";
import {
	FEED_LIMIT,
	type NotificationItem,
	type NotificationSource,
	SEVERITY,
	titleOf,
} from "./NotificationBell";
import { EmptyState, PageHeader } from "./page";
import { type Column, DataTable, useCursorPagination } from "./table";

/**
 * The in-app inbox (EP12-S03, DA-12-04): the bell's feed and the `/inbox`
 * page, shared by the Admin Console and the Store Portal.
 *
 * **Every read is under one key**, `["notifications"]`, so the live stream's
 * `notification.created` (and the 60-second poll behind a stream that is
 * down) re-reads the badge, the bell's ten and the page at once.
 */

/** The cache key every inbox read sits under. */
export const INBOX_QUERY_KEY = ["notifications"] as const;

/** The stream topic that says my inbox changed. */
export const TOPIC_NOTIFICATION_CREATED = "notification.created";

/** The topics a row can carry: its alert's category. */
export const INBOX_TOPICS = [
	"orders",
	"inventory",
	"finance",
	"integrations",
	"delivery",
	"system",
];

/** A server row as the bell draws it. */
export function toItem(n: InboxNotification): NotificationItem {
	return {
		id: n.id,
		topic: n.topic,
		severity: n.severity,
		title: n.title,
		count: n.count,
		...(n.deepLink === undefined ? {} : { deepLink: n.deepLink }),
		readAt: n.readAt ?? null,
		createdAt: n.createdAt,
	};
}

/**
 * The bell's source over the inbox API. Marking read re-reads every inbox
 * query, so the badge and the page agree at once.
 */
export function useInboxSource(api: Client): NotificationSource {
	const queries = useQueryClient();
	return useMemo(() => {
		const refresh = () => queries.invalidateQueries({ queryKey: INBOX_QUERY_KEY });
		return {
			useFeed() {
				const count = useQuery({
					queryKey: [...INBOX_QUERY_KEY, "unread-count"],
					queryFn: ({ signal }) => api.getUnreadNotificationCount({ signal }),
				});
				const latest = useQuery({
					queryKey: [...INBOX_QUERY_KEY, "latest"],
					queryFn: ({ signal }) => api.listNotifications({ limit: FEED_LIMIT, signal }),
				});
				return {
					unreadCount: count.data ?? 0,
					latest: (latest.data?.items ?? []).map(toItem),
					status: count.isError ? "error" : count.isPending ? "loading" : "ready",
				};
			},
			async markAllRead() {
				await api.markNotificationsRead({ all: true });
				await refresh();
			},
			async markRead(id: string) {
				await api.markNotificationsRead({ ids: [id] });
				await refresh();
			},
		};
	}, [api, queries]);
}

export interface InboxPageProps {
	api: Client;
	t: Translate;
	locale: Locale;
	timeZone: string;
	/** The router's navigate, for a row's deep link. */
	onNavigate: (to: string) => void;
}

type SeverityFilter = "all" | InboxSeverity;

/**
 * `/inbox`: every notification, newest first, filtered by severity, topic
 * and unread-only. Opening a row marks it read and follows its link; a link
 * to something since deleted lands on the app's "not found", already read.
 */
export function InboxPage({ api, t, locale, timeZone, onNavigate }: InboxPageProps) {
	const source = useInboxSource(api);
	const [severity, setSeverity] = useState<SeverityFilter>("all");
	const [topic, setTopic] = useState("all");
	const [unreadOnly, setUnreadOnly] = useState(false);

	const unread = useQuery({
		queryKey: [...INBOX_QUERY_KEY, "unread-count"],
		queryFn: ({ signal }) => api.getUnreadNotificationCount({ signal }),
	});
	const table = useCursorPagination<InboxNotification>(
		(cursor, limit, signal) =>
			api
				.listNotifications({
					...(severity === "all" ? {} : { severity }),
					...(topic === "all" ? {} : { topic }),
					...(unreadOnly ? { unread: true } : {}),
					...(cursor === null ? {} : { cursor }),
					limit,
					signal,
				})
				.then((page) => ({ items: page.items, nextCursor: page.nextCursor ?? null })),
		{ queryKey: [...INBOX_QUERY_KEY, "page", severity, topic, unreadOnly], limit: 25 },
	);

	const open = (n: InboxNotification) => {
		if (n.readAt === undefined) {
			void source.markRead?.(n.id);
		}
		if (n.deepLink !== undefined) {
			onNavigate(n.deepLink);
		}
	};

	const columns: Column<InboxNotification>[] = [
		{
			id: "severity",
			headerKey: "ui.inbox.columns.severity",
			width: 120,
			cell: (n) => {
				const tone = SEVERITY[n.severity] ?? SEVERITY.info;
				return (
					<span className="flex items-center gap-1.5 text-[12.5px]">
						<Icon as={tone.icon} size={15} aria-hidden="true" className={tone.className} />
						{t(`ui.inbox.severity.${n.severity}`)}
					</span>
				);
			},
		},
		{
			id: "title",
			headerKey: "ui.inbox.columns.title",
			card: "wide",
			cell: (n) => (
				<span className="flex flex-col">
					<span className={cx("text-fg", n.readAt === undefined && "font-bold")}>
						{titleOf(n, t)}
					</span>
					{n.body !== undefined && <span className="text-[12px] text-fg-subtle">{n.body}</span>}
				</span>
			),
		},
		{
			id: "entity",
			headerKey: "ui.inbox.columns.entity",
			mono: true,
			hideable: true,
			cell: (n) => n.entityRef ?? "—",
		},
		{
			id: "received",
			headerKey: "ui.inbox.columns.received",
			cell: (n) => formatDateTime(n.createdAt, { locale, timeZone }),
		},
	];

	const count = unread.data ?? 0;

	return (
		<div className="flex flex-col gap-4">
			<PageHeader
				title={t("ui.inbox.title")}
				chip={
					count > 0 ? <Badge tone="accent">{t("ui.inbox.unread", { count })}</Badge> : undefined
				}
				breadcrumb={[{ label: t("ui.inbox.title") }]}
				primaryAction={
					<Button
						variant="secondary"
						disabled={count === 0}
						onClick={() => void source.markAllRead?.()}
					>
						{t("ui.notifications.markAll")}
					</Button>
				}
			/>
			<DataTable<InboxNotification>
				screenKey="inbox"
				caption={t("ui.inbox.title")}
				t={t}
				columns={columns}
				rowKey={(n) => n.id}
				page={table.page}
				status={table.status}
				error={table.error}
				onRetry={table.refetch}
				onRowClick={open}
				rowActions={(n) =>
					n.readAt === undefined ? (
						<Button
							variant="ghost"
							size="sm"
							onClick={(event) => {
								event.stopPropagation();
								void source.markRead?.(n.id);
							}}
						>
							{t("ui.inbox.markRead")}
						</Button>
					) : null
				}
				toolbar={
					<>
						<SegmentedControl<SeverityFilter>
							aria-label={t("ui.inbox.columns.severity")}
							value={severity}
							onChange={(next) => {
								setSeverity(next);
								table.reset();
							}}
							options={(["all", "critical", "action_needed", "info"] as const).map((value) => ({
								value,
								label: t(value === "all" ? "ui.inbox.all" : `ui.inbox.severity.${value}`),
							}))}
						/>
						<Select
							aria-label={t("ui.inbox.topic")}
							// A toolbar control, not a form field: full width on a phone,
							// a fixed width beside the other filters from 640px up.
							className="sm:w-56"
							value={topic}
							onValueChange={(next) => {
								setTopic(next);
								table.reset();
							}}
							options={[
								{ value: "all", label: t("ui.inbox.allTopics") },
								...INBOX_TOPICS.map((value) => ({ value, label: t(`ui.inbox.topics.${value}`) })),
							]}
						/>
						<Switch
							checked={unreadOnly}
							onCheckedChange={(next) => {
								setUnreadOnly(next);
								table.reset();
							}}
							label={t("ui.inbox.unreadOnly")}
						/>
					</>
				}
				pagination={{
					showingFrom: table.showingFrom,
					showingTo: table.showingTo,
					total: table.total,
					hasNext: table.hasNext,
					hasPrevious: table.hasPrevious,
					onNext: table.next,
					onPrevious: table.previous,
				}}
				collapse="cards"
				empty={<EmptyState title={t("ui.inbox.emptyTitle")} body={t("ui.inbox.emptyBody")} />}
			/>
		</div>
	);
}
